import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
// Theme will be applied via CSS class

import { Constants } from '@eagerdog/constants';
import { httpService } from '../../services/http.service';
import { apiService } from '../../services/api.service';
import { toast } from '../../components/Toast/ToastManager';
import { Modal, useModal } from '../../components/Modal/Modal';
import { useDebounce } from '../../hooks/useDebounce';
import { Config } from '@eagerdog/config';

import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';


import './AdminPoints.scss';

interface IProps {}

interface IUser {
  _id: string;
  first_name?: string;
  last_name?: string;
  email: string;
}

interface IWalletTransaction {
  _id: string;
  user_id: string | { _id: string; first_name: string; last_name: string; display_name?: string; email: string };
  transaction_type: string;
  points: number;
  description?: string;
  created_at?: string;
}

interface ITransactionRow {
  _id: string;
  date: string;
  userName: string;
  userEmail: string;
  status: string;
  points: string;
  description: string;
  transaction_type: string;
  user_id: string | { _id: string; first_name: string; last_name: string; display_name?: string; email: string };
  created_at?: string;
}

interface IClubAnalytics {
  totalPointsAwarded: number;
  totalPointsRedeemed: number;
  totalDollarsAwarded: number;
  totalDollarsRedeemed: number;
  totalTransactions: number;
  uniqueUserCount: number;
}

const AdminPoints: React.FC<IProps> = () => {
  const { clubId } = useParams<{ clubId: string }>();
  
  // State
  const [loading, setLoading] = useState<boolean>(true);
  const [analytics, setAnalytics] = useState<IClubAnalytics | null>(null);
  const [error, setError] = useState<string>('');
  
  // AGGrid state
  const [rowData, setRowData] = useState<ITransactionRow[]>([]);

  // Award points modal state
  const { isShown: showAwardModal, toggle: toggleAwardModal } = useModal();
  const [awardLoading, setAwardLoading] = useState<boolean>(false);
  
  // Edit transaction modal state
  const { isShown: showEditModal, toggle: toggleEditModal } = useModal();
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [editingTransaction, setEditingTransaction] = useState<IWalletTransaction | null>(null);
  const [editPoints, setEditPoints] = useState<string>('');

  // Load data function
  const loadData = useCallback(async () => {
    if (!clubId) return;
    
    setLoading(true);
    setError('');
    
    try {
      const [allTransactionsResponse, displayTransactionsResponse] = await Promise.all([
        httpService.get(`${Config.web.REACT_APP_API_URL}/api/wallets/clubs/${clubId}/transactions?limit=1000`), // All transactions for analytics
        httpService.get(`${Config.web.REACT_APP_API_URL}/api/wallets/clubs/${clubId}/transactions?limit=50`), // Recent transactions for display
      ]);
      const allValidTransactions = allTransactionsResponse.data as IWalletTransaction[];
      const validDisplayTransactions = displayTransactionsResponse.data as IWalletTransaction[];

      // Calculate analytics from all filtered transactions
      const totalPointsAwarded = allValidTransactions
        .filter((t: IWalletTransaction) => t.transaction_type === Constants.wallet_transaction_type.earn)
        .reduce((sum: number, t: IWalletTransaction) => sum + t.points, 0);
        
      const totalPointsRedeemed = allValidTransactions
        .filter((t: IWalletTransaction) => t.transaction_type === Constants.wallet_transaction_type.redeem)
        .reduce((sum: number, t: IWalletTransaction) => sum + Math.abs(t.points), 0);
        
      const uniqueUserCount = new Set(allValidTransactions.map((t: IWalletTransaction) => 
        typeof t.user_id === 'string' ? t.user_id : t.user_id._id
      )).size;

      const calculatedAnalytics: IClubAnalytics = {
        totalPointsAwarded,
        totalPointsRedeemed,
        totalDollarsAwarded: totalPointsAwarded / Constants.wallet.points_to_dollar_ratio,
        totalDollarsRedeemed: totalPointsRedeemed / Constants.wallet.points_to_dollar_ratio,
        uniqueUserCount,
        totalTransactions: allValidTransactions.length
      };

      setAnalytics(calculatedAnalytics);
      
      // Transform display transactions for AGGrid
      const transformedRows: ITransactionRow[] = validDisplayTransactions.map((transaction: IWalletTransaction) => {
        const formatDate = (dateString?: string) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          });
        };

        const formatTransactionType = (type: string) => {
          switch (type) {
            case Constants.wallet_transaction_type.earn: return 'Earned';
            case Constants.wallet_transaction_type.redeem: return 'Redeemed';
            case Constants.wallet_transaction_type.adjustment: return 'Adjustment';
            default: return type;
          }
        };

        const getUserName = (userId: string | { _id: string; first_name: string; last_name: string; display_name?: string; email: string }) => {
          // If it's already an object with user data, use it
          if (typeof userId === 'object' && userId.first_name && userId.last_name) {
            return `${userId.first_name} ${userId.last_name}`;
          } else if (typeof userId === 'object' && userId.display_name) {
            return userId.display_name;
          } else if (typeof userId === 'object' && userId.email) {
            return userId.email;
          }
          if (typeof userId === 'string') return userId;
          return 'Unknown User';
        };

        const getUserEmail = (userId: string | { _id: string; first_name: string; last_name: string; display_name?: string; email: string }) => {
          // If it's already an object with user data, use it
          if (typeof userId === 'object' && userId.email) {
            return userId.email;
          }
          return '-';
        };

        return {
          _id: transaction._id,
          date: formatDate(transaction.created_at),
          userName: getUserName(transaction.user_id),
          userEmail: getUserEmail(transaction.user_id),
          status: formatTransactionType(transaction.transaction_type),
          points: `${transaction.points > 0 ? '+' : ''}${transaction.points.toLocaleString()}`,
          description: transaction.description || '-',
          transaction_type: transaction.transaction_type,
          user_id: transaction.user_id,
          created_at: transaction.created_at
        };
      });
      
      setRowData(transformedRows);
    } catch (error: any) {
      console.error('Error loading points data:', error);
      setError('Failed to load points data. Please try again.');
      toast.show({
        title: "Error",
        content: "Failed to load points data",
        duration: 5000,
        type: "fail"
      });
    } finally {
      setLoading(false);
    }
  }, [clubId]);


  // AGGrid column definitions
  const colDefs: ColDef<ITransactionRow>[] = [
    { 
      field: "date", 
      headerName: "Date", 
      minWidth: 120, 
      filter: true,
      sortable: true 
    },
    { 
      field: "userName", 
      headerName: "User", 
      minWidth: 180, 
      filter: "agTextColumnFilter",
      sortable: true 
    },
    { 
      field: "userEmail", 
      headerName: "Email", 
      minWidth: 200, 
      filter: "agTextColumnFilter",
      sortable: true 
    },
    { 
      field: "status", 
      headerName: "Status", 
      minWidth: 100, 
      filter: true,
      sortable: true,
      cellClass: (params) => {
        if (!params.data) return '';
        if (params.data.transaction_type === Constants.wallet_transaction_type.earn) {
          return 'transaction-earn';
        } else if (params.data.transaction_type === Constants.wallet_transaction_type.redeem) {
          return 'transaction-redeem';
        } else if (params.data.transaction_type === Constants.wallet_transaction_type.adjustment) {
          return 'transaction-adjustment';
        }
        return '';
      }
    },
    { 
      field: "points", 
      headerName: "Points", 
      minWidth: 120, 
      filter: true,
      sortable: true,
      cellStyle: (params) => {
        if (!params.data) return null;
        const points = parseInt(params.data.points.replace(/[^\d-]/g, ''));
        return {
          color: points > 0 ? '#16a085' : '#e74c3c',
          fontWeight: '500'
        };
      }
    },
    { 
      field: "description", 
      headerName: "Description", 
      minWidth: 200, 
      filter: "agTextColumnFilter",
      sortable: true 
    }
  ];

  const defaultColDef: ColDef = {
    flex: 1,
    resizable: true
  };

  const onGridReady = () => {
    // Grid ready handler
  };
  const [editDescription, setEditDescription] = useState<string>('');
  
  // User search states
  const [emailSearch, setEmailSearch] = useState<string>('');
  const [userList, setUserList] = useState<IUser[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  
  // Award form states
  const [points, setPoints] = useState<string>('');
  const [dollars, setDollars] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [userClubPoints, setUserClubPoints] = useState<number | null>(null);
  const [loadingUserPoints, setLoadingUserPoints] = useState<boolean>(false);
  
  const dEmailSearch = useDebounce<string>(emailSearch, 800);



  // Search users function - only show users with club capacity (volunteer, member, admin)
  const searchUsers = useCallback(async (userEmail: string) => {
    if (!userEmail.trim() || !clubId) {
      setUserList([]);
      return;
    }

    setSearchLoading(true);

    try {
      const result = await apiService.searchClubUsersByEmail(
        clubId,
        userEmail,
        [
          Constants.capacity_type.volunteer,
          Constants.capacity_type.member,
          Constants.capacity_type.admin
        ],
        1,
        5
      );

      const users = result.users
        .filter((record) => !!record.user?.email)
        .map((record) => ({
          _id: record.user_id,
          email: record.user.email || '',
          first_name: record.user.first_name,
          last_name: record.user.last_name,
        }));

      setUserList(users);
    } catch (error) {
      console.error('Error searching club users:', error);
      setUserList([]);
    } finally {
      setSearchLoading(false);
    }
  }, [clubId]);

  // Handle user selection
  const handleUserSelect = async (user: IUser) => {
    setSelectedUser(user);
    setEmailSearch(user.email || '');
    setUserList([]);
    setFormErrors({});
    
    // Fetch user's current club points
    if (clubId) {
      setLoadingUserPoints(true);
      try {
        const pointsData = await apiService.getUserClubPoints(user._id, clubId);
        setUserClubPoints(pointsData.points);
      } catch (error) {
        console.error('Error fetching user club points:', error);
        setUserClubPoints(0); // Default to 0 if error
      } finally {
        setLoadingUserPoints(false);
      }
    }
  };

  // Points <-> dollars conversion (1000 points = $1). Keep both inputs in sync.
  const POINTS_PER_DOLLAR = 1000;

  const handlePointsChange = (value: string) => {
    setPoints(value);
    if (value === '' || value === '-' || isNaN(Number(value))) {
      setDollars('');
      return;
    }
    const dollarValue = Number(value) / POINTS_PER_DOLLAR;
    setDollars(String(Number(dollarValue.toFixed(4))));
  };

  const handleDollarsChange = (value: string) => {
    setDollars(value);
    if (value === '' || value === '-' || isNaN(Number(value))) {
      setPoints('');
      return;
    }
    const pointsValue = Math.round(Number(value) * POINTS_PER_DOLLAR);
    setPoints(String(pointsValue));
  };

  // Award points function
  const handleAwardPoints = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    const errors: {[key: string]: string} = {};
    if (!selectedUser) errors.user = 'Please select a user';
    
    const pointsValue = Number(points);
    if (!points || isNaN(pointsValue) || pointsValue === 0) {
      errors.points = 'Please enter a valid number of points (positive to award, negative to remove)';
    }
    
    // Maximum of $500 (500,000 points) per award/removal
    if (Math.abs(pointsValue) > 500000) {
      errors.points = 'Maximum of $500 (500,000 points) per transaction';
    }

    // Check if trying to remove more points than user has
    if (pointsValue < 0 && userClubPoints !== null) {
      const pointsToRemove = Math.abs(pointsValue);
      if (pointsToRemove > userClubPoints) {
        errors.points = `Cannot remove ${pointsToRemove.toLocaleString()} points. User only has ${userClubPoints.toLocaleString()} points from this club.`;
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setAwardLoading(true);
    
    try {
      await httpService.post(`${Config.web.REACT_APP_API_URL}/api/wallets/award`, {
        user_id: selectedUser!._id,
        club_id: clubId,
        points: pointsValue,
        description: description || undefined
      });
      
      const actionText = pointsValue > 0 ? 'Awarded' : 'Removed';
      const absPoints = Math.abs(pointsValue);
      
      toast.show({
        title: "Success",
        content: `${actionText} ${absPoints.toLocaleString()} points ${pointsValue > 0 ? 'to' : 'from'} ${selectedUser!.first_name} ${selectedUser!.last_name}`,
        duration: 5000,
        type: "success"
      });
      
      // Reset form and reload data
      setSelectedUser(null);
      setEmailSearch('');
      setPoints('');
      setDollars('');
      setDescription('');
      setFormErrors({});
      setUserClubPoints(null);
      toggleAwardModal();
      loadData();
    } catch (error: any) {
      console.error('Error awarding points:', error);
      toast.show({
        title: "Error",
        content: error?.response?.data?.message || "Failed to process points",
        duration: 5000,
        type: "fail"
      });
    } finally {
      setAwardLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (dEmailSearch !== emailSearch) {
      searchUsers(emailSearch);
    }
  }, [emailSearch, dEmailSearch, searchUsers]);



  const handleUpdateTransaction = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingTransaction) return;
    
    // Validation
    const errors: {[key: string]: string} = {};
    if (!editPoints || isNaN(Number(editPoints)) || Number(editPoints) <= 0) {
      errors.points = 'Please enter a valid positive number of points';
    }
    if (Number(editPoints) > 500000) {
      errors.points = 'Maximum of $500 (500,000 points) per transaction';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setEditLoading(true);
    
    try {
      await httpService.put(`${Config.web.REACT_APP_API_URL}/api/wallets/transactions/${editingTransaction._id}`, {
        points: Number(editPoints),
        description: editDescription || undefined
      });
      
      toast.show({
        title: "Success",
        content: "Transaction updated successfully",
        duration: 5000,
        type: "success"
      });
      
      toggleEditModal();
      setEditingTransaction(null);
      setEditPoints('');
      setEditDescription('');
      setFormErrors({});
      loadData();
    } catch (error: any) {
      console.error('Error updating transaction:', error);
      toast.show({
        title: "Error",
        content: error?.response?.data?.message || "Failed to update transaction",
        duration: 5000,
        type: "fail"
      });
    } finally {
      setEditLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="AdminPoints">
        <header className="pointsHeader">
          <h1>Points</h1>
          <p className="subtitle">Manage volunteer points and rewards</p>
        </header>

        <div className="pointsContent">
          {/* Analytics Skeleton */}
          <div className="analyticsSection">
            <h2>Club Points Overview</h2>
            <div className="analyticsGrid">
              <div className="analyticCard skeleton">
                <div className="cardValue skeleton-text"></div>
                <div className="cardLabel skeleton-text"></div>
                <div className="cardSubLabel skeleton-text"></div>
              </div>
              <div className="analyticCard skeleton">
                <div className="cardValue skeleton-text"></div>
                <div className="cardLabel skeleton-text"></div>
                <div className="cardSubLabel skeleton-text"></div>
              </div>
              <div className="analyticCard skeleton">
                <div className="cardValue skeleton-text"></div>
                <div className="cardLabel skeleton-text"></div>
                <div className="cardSubLabel skeleton-text"></div>
              </div>
            </div>
          </div>

          {/* Transactions Skeleton */}
          <div className="transactionsSection">
            <h2>Recent Transactions</h2>
            <div className="tableWrap">
              <div className="transactionsSkeleton">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="skeletonRow">
                    <div className="skeletonCell"></div>
                    <div className="skeletonCell"></div>
                    <div className="skeletonCell"></div>
                    <div className="skeletonCell"></div>
                    <div className="skeletonCell"></div>
                    <div className="skeletonCell"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="AdminPoints">
        <header className="pointsHeader">
          <h1>Points</h1>
          <p className="subtitle">Manage volunteer points and rewards</p>
        </header>
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="AdminPoints">
      <header className="pointsHeader">
        <h1>Points</h1>
        <p className="subtitle">Manage volunteer points and rewards</p>
      </header>

      <div className="pointsContent">
        {/* Analytics Overview */}
        {analytics && (
          <div className="analyticsSection">
            <h2>Club Points Overview</h2>
            <div className="analyticsGrid">
              <div className="analyticCard">
                <div className="cardValue">{analytics.totalPointsAwarded.toLocaleString()}</div>
                <div className="cardLabel">Points Awarded</div>
                <div className="cardSubLabel">${analytics.totalDollarsAwarded.toFixed(2)} value</div>
              </div>
              <div className="analyticCard">
                <div className="cardValue">{analytics.totalPointsRedeemed.toLocaleString()}</div>
                <div className="cardLabel">Points Redeemed</div>
                <div className="cardSubLabel">${analytics.totalDollarsRedeemed.toFixed(2)} value</div>
              </div>
              <div className="analyticCard">
                <div className="cardValue">{analytics.uniqueUserCount}</div>
                <div className="cardLabel">Active Volunteers</div>
                <div className="cardSubLabel">{analytics.totalTransactions} transactions</div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Transactions */}
        <div className="transactionsSection">
          <div className="transactionsHeader">
            <h2>Recent Transactions</h2>
            <Button onClick={toggleAwardModal}>Award Points</Button>
          </div>
          {rowData.length > 0 ? (
            <div className="tableWrap">
              <div className={`ag-theme-quartz`} style={{ height: '400px', width: '100%' }}>
                <AgGridReact
                  rowData={rowData}
                  columnDefs={colDefs}
                  onGridReady={onGridReady}
                  pagination={true}
                  paginationPageSize={20}
                  defaultColDef={defaultColDef}
                  overlayNoRowsTemplate="<span style='padding: 20px; font-size: 16px; color: #666;'>No point transactions yet</span>"
                />
              </div>
            </div>
          ) : (
            <div className="emptyState">
              <p>No point transactions yet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Award Points Modal */}
      <Modal
        isShown={showAwardModal}
        hide={toggleAwardModal}
        className="awardPointsModal"
        modalContent={
          <div className="awardPointsContent">
            <h2>Award Points</h2>
            <p>Award points to volunteers for their work</p>
            
            <form onSubmit={handleAwardPoints}>
              {!selectedUser ? (
                                  <div className="userSearchSection">
                  <div className="formGroup">
                    <label htmlFor="emailSearch">Search Club Member by Email *</label>
                    <Input
                      id="emailSearch"
                      type="email"
                      value={emailSearch}
                      onChange={(e) => setEmailSearch(e.target.value)}
                      placeholder="Search volunteers, members, or admins..."
                      autoComplete="off"
                    />
                    <div className="helpText">
                      Only users with volunteer, member, or admin roles in this club can receive points.
                    </div>
                  </div>

                  {searchLoading && (
                    <div className="searchLoading">Searching users...</div>
                  )}

                  {!searchLoading && emailSearch.length > 0 && userList.length === 0 && (
                    <div className="noUserFound">
                      <span>No club members found matching "{emailSearch}"</span>
                      <div className="helpText">
                        Make sure the user has volunteer, member, or admin roles in this club.
                      </div>
                    </div>
                  )}

                  {!searchLoading && userList.length > 0 && (
                    <ul className="usersFound">
                      {userList.map((user: IUser) => (
                        <li key={user._id} onClick={() => handleUserSelect(user)}>
                          <div className="userInfo">
                            <div className="userName">{user.first_name || ''} {user.last_name || ''}</div>
                            <div className="userEmail">{user.email || ''}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {formErrors.user && <div className="fieldError">{formErrors.user}</div>}
                </div>
              ) : (
                <div className="selectedUserSection">
                  <div className="formGroup">
                    <label>Selected User</label>
                    <div className="selectedUser">
                      <div className="userName">{selectedUser.first_name} {selectedUser.last_name}</div>
                      <div className="userEmail">{selectedUser.email}</div>
                      {loadingUserPoints ? (
                        <div className="userBalance">Loading current balance...</div>
                      ) : userClubPoints !== null && (
                        <div className="userBalance">Current Balance: {userClubPoints.toLocaleString()} points</div>
                      )}
                      <button 
                        type="button" 
                        className="changeUser"
                        onClick={() => {
                          setSelectedUser(null);
                          setEmailSearch('');
                          setUserClubPoints(null);
                        }}
                      >
                        Change User
                      </button>
                    </div>
                  </div>

                  <div className="formGroup">
                    <label htmlFor="dollars">Dollar Amount ($)</label>
                    <Input
                      id="dollars"
                      type="number"
                      value={dollars}
                      onChange={(e) => handleDollarsChange(e.target.value)}
                      placeholder="Enter dollar amount (positive to award, negative to remove)"
                    />
                    <div className="helpText">
                      Enter a dollar amount and the points will be calculated automatically. $1 = 1000 points
                    </div>
                  </div>

                  <div className="formGroup">
                    <label htmlFor="points">Points to Award *</label>
                    <Input
                      id="points"
                      type="number"
                      value={points}
                      onChange={(e) => handlePointsChange(e.target.value)}
                      placeholder="Enter points (positive to award, negative to remove)"
                    />
                    <div className="helpText">
                      Enter a positive number to award points or a negative number to remove points. 1000 points = $1
                    </div>
                    {formErrors.points && <div className="fieldError">{formErrors.points}</div>}
                  </div>

                  <div className="formGroup">
                    <label htmlFor="description">Description (Optional)</label>
                    <Input
                      id="description"
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="E.g., Event setup volunteer work or Removed"
                    />
                  </div>

                  <div className="formActions">
                    <Button type="button" onClick={toggleAwardModal}>Cancel</Button>
                    <Button type="submit" disabled={awardLoading} isLoading={awardLoading}>
                      Award Points
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </div>
        }
      />

      {/* Edit Transaction Modal */}
      <Modal
        isShown={showEditModal}
        hide={toggleEditModal}
        className="editTransactionModal"
        modalContent={
          <div className="editTransactionContent">
            <h2>Edit Transaction</h2>
            <p>Update the transaction details</p>
            
            <form onSubmit={handleUpdateTransaction}>
              <div className="formGroup">
                <label htmlFor="editPoints">Points *</label>
                <Input
                  id="editPoints"
                  type="number"
                  value={editPoints}
                  onChange={(e) => setEditPoints(e.target.value)}
                  placeholder="Enter point amount"
                  min="1"
                  required
                />
                {formErrors.points && <div className="errorMessage">{formErrors.points}</div>}
              </div>

              <div className="formGroup">
                <label htmlFor="editDescription">Description (Optional)</label>
                <Input
                  id="editDescription"
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="E.g., Event setup volunteer work"
                />
              </div>

              <div className="formActions">
                <Button type="button" onClick={toggleEditModal}>Cancel</Button>
                <Button type="submit" disabled={editLoading} isLoading={editLoading}>
                  Update Transaction
                </Button>
              </div>
            </form>
          </div>
        }
      />
    </div>
  );
};

export default AdminPoints;
